// Google Analytics 4 Service
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

class AnalyticsService {
  private trackingId: string;
  private isReady: boolean = false;

  constructor() {
    // Get tracking ID from environment variable or use your actual ID as fallback
    this.trackingId = process.env.VUE_APP_GA_TRACKING_ID || 'G-RZ50C0X7DP';
  }

  // Wait for GA4 to be ready
  private async waitForGA(): Promise<void> {
    if (this.isReady) return;
    
    return new Promise((resolve) => {
      const checkGA = () => {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          this.isReady = true;
          resolve();
        } else {
          setTimeout(checkGA, 100);
        }
      };
      checkGA();
    });
  }

  // Initialize GA4
  async init() {
    await this.waitForGA();
    
    if (this.isReady && typeof window.gtag === 'function') {
      window.gtag('config', this.trackingId, {
        page_title: document.title,
        page_location: window.location.href,
      });
      console.log('📊 Google Analytics initialized');
    }
  }

  // Track page view
  async trackPageView(path: string, title?: string) {
    await this.waitForGA();
    
    if (this.isReady && typeof window.gtag === 'function') {
      console.log('📊 Tracking page view:', path, title);
      window.gtag('config', this.trackingId, {
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href,
      });
    }
  }

  // Track custom events
  async trackEvent(eventName: string, parameters?: Record<string, any>) {
    await this.waitForGA();
    
    if (this.isReady && typeof window.gtag === 'function') {
      console.log('📊 Tracking event:', eventName, parameters);
      window.gtag('event', eventName, parameters);
    }
  }

  // Track user engagement
  async trackUserEngagement(action: string, category: string, label?: string, value?: number) {
    await this.trackEvent('user_engagement', {
      action,
      category,
      label,
      value,
    });
  }

  // Track button clicks
  async trackButtonClick(buttonName: string, location?: string) {
    await this.trackEvent('button_click', {
      button_name: buttonName,
      location: location || 'unknown',
    });
  }

  // Track form submissions
  async trackFormSubmission(formName: string, success: boolean) {
    await this.trackEvent('form_submit', {
      form_name: formName,
      success,
    });
  }

  // Track user authentication
  async trackAuth(action: 'sign_in' | 'sign_out' | 'sign_up', method?: string) {
    await this.trackEvent('user_authentication', {
      action,
      method: method || 'unknown',
    });
  }
}

export const analytics = new AnalyticsService();
export default analytics; 